import { Fragment } from "preact";
import type { ComponentChildren, VNode } from "preact";

/**
 * Tiny inline markdown renderer for assistant chat messages.
 *
 * Why not pull in `marked` / `markdown-it` / `react-markdown`?
 *   - Bundle size: those libs are 30-100KB even minified. The whole widget
 *     bundle is currently ~37KB; we're not adding 100KB to render bold.
 *   - We only need a tiny feature set: bold, italic, code, paragraphs,
 *     line breaks, simple lists. Claude's chat output uses nothing more.
 *   - Building a Preact VNode tree directly (no innerHTML, no DOMPurify
 *     dependency) gives us XSS-safe-by-construction. No string ever
 *     becomes HTML — every `<`/`>` is a real Preact element.
 *
 * Feature set (intentional):
 *   - **bold**, __bold__              → <strong>
 *   - *italic*, _italic_              → <em>
 *   - `code`                          → <code>
 *   - blank line                      → paragraph break
 *   - single newline                  → <br>
 *   - lines starting with `- ` / `* ` → <ul><li>
 *   - lines starting with `1. ` etc.  → <ol><li>
 *
 * Out of scope (silently ignored — render as literal text):
 *   - links (Claude rarely uses inline links here; product cards handle URLs)
 *   - images, tables, headings, blockquotes, code fences, html
 *
 * Streaming-safe: callers pass a partial buffer mid-stream and we render
 * what we have. An unclosed `**` stays as `**` until the closing pair
 * arrives — the regex requires both sides — which means the user briefly
 * sees the asterisks. That's strictly better than seeing them forever.
 */
export function renderMarkdown(text: string): VNode {
  if (!text) return <Fragment />;

  // Split on blank lines into "blocks" (paragraphs / lists). A blank line
  // is two or more consecutive newlines.
  const blocks = text.split(/\n{2,}/);
  const out: VNode[] = [];

  for (let i = 0; i < blocks.length; i++) {
    const block = blocks[i];
    if (!block.trim()) continue;

    // Detect list blocks: every non-empty line starts with a bullet
    // ("- " / "* ") or a number marker ("1. ").
    const lines = block.split("\n");
    const isUnordered = lines.every(
      (ln) => /^\s*[-*]\s+/.test(ln) || ln.trim() === ""
    );
    const isOrdered = lines.every(
      (ln) => /^\s*\d+\.\s+/.test(ln) || ln.trim() === ""
    );

    if (isUnordered) {
      out.push(
        <ul key={i} class="md-list">
          {lines
            .filter((ln) => ln.trim() !== "")
            .map((ln, j) => (
              <li key={j}>{renderInline(ln.replace(/^\s*[-*]\s+/, ""))}</li>
            ))}
        </ul>
      );
      continue;
    }

    if (isOrdered) {
      out.push(
        <ol key={i} class="md-list">
          {lines
            .filter((ln) => ln.trim() !== "")
            .map((ln, j) => (
              <li key={j}>{renderInline(ln.replace(/^\s*\d+\.\s+/, ""))}</li>
            ))}
        </ol>
      );
      continue;
    }

    // Default: paragraph with single-newline → <br>.
    const children: ComponentChildren[] = [];
    for (let j = 0; j < lines.length; j++) {
      if (j > 0) children.push(<br key={`br-${j}`} />);
      children.push(<Fragment key={`ln-${j}`}>{renderInline(lines[j])}</Fragment>);
    }
    out.push(
      <p key={i} class="md-p">
        {children}
      </p>
    );
  }

  return <Fragment>{out}</Fragment>;
}

/**
 * Inline tokenizer. Produces an array of text and element nodes. We scan
 * left-to-right looking for the *next* delimiter at any position; whatever
 * comes before it is plain text. The set of delimiters we recognize:
 *
 *   `…`   inline code (highest priority — code spans are literal inside)
 *   **…** strong
 *   __…__ strong
 *   *…*   emphasis (won't double-match because **…** is checked first)
 *   _…_   emphasis
 *
 * Order matters: longest-prefix delimiters come first. A simple one-pass
 * regex with alternation handles this without backtracking.
 */
function renderInline(text: string): VNode {
  const nodes: ComponentChildren[] = [];
  let cursor = 0;

  // One regex with alternation: code | **strong** | __strong__ | *em* | _em_.
  // Inline code is matched first (it can contain * and _ literally).
  const re = /(`)([^`\n]+?)\1|(\*\*|__)([\s\S]+?)\3|(\*|_)([^*_\n]+?)\5/g;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = re.exec(text)) !== null) {
    const start = match.index;
    if (start > cursor) {
      nodes.push(text.slice(cursor, start));
    }
    if (match[1]) {
      nodes.push(
        <code key={key++} class="md-code">
          {match[2]}
        </code>
      );
    } else if (match[3]) {
      nodes.push(<strong key={key++}>{match[4]}</strong>);
    } else if (match[5]) {
      nodes.push(<em key={key++}>{match[6]}</em>);
    }
    cursor = re.lastIndex;
  }

  if (cursor < text.length) {
    nodes.push(text.slice(cursor));
  }

  return <Fragment>{nodes}</Fragment>;
}
